import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { trackOTOPurchase } from "@/lib/metaPixel";
import { CheckCircle, ArrowRight, CalendarCheck } from "@phosphor-icons/react";

/**
 * /join/book-call — post-Roadmap-Call-purchase Calendly booking page.
 *
 * User has just paid $97 for the Roadmap Call via /funnel/oto-checkout. Now
 * they need to actually book a slot. We embed Coach Jimmy's Calendly in a full
 * iframe; when they're done (or want to book later), a "Continue into the app"
 * button routes them to /join/welcome.
 *
 * If a user hits this page directly without `?paid=1` (i.e. didn't come from
 * a successful Stripe redirect), we still show the Calendly — no harm in an
 * extra booking. Access to the app itself is gated by trial status, not this
 * page.
 */

const CALENDLY_URL = "https://calendly.com/jimmy-lazyleanmethod/lazy-lean";

export default function JoinBookCallPage() {
  const [params] = useSearchParams();
  const paid = params.get("paid") === "1";
  const [ready, setReady] = useState(false);

  // Load Calendly widget script on mount (only if we have a URL)
  useEffect(() => {
    if (!CALENDLY_URL) return;
    const s = document.createElement("script");
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    s.onload = () => setReady(true);
    document.body.appendChild(s);
    return () => { document.body.removeChild(s); };
  }, []);

  // Fire Meta Pixel Purchase event for the $97 OTO. Only fires when we arrived
  // here via `?paid=1` from the Stripe redirect (skip organic/direct hits).
  // De-duped via sessionStorage so a refresh doesn't double-fire.
  useEffect(() => {
    if (!paid) return;
    const key = "_llm_pixel_oto_fired";
    if (sessionStorage.getItem(key)) return;
    trackOTOPurchase();
    sessionStorage.setItem(key, "1");
  }, [paid]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="border-b border-[#262626] py-4 px-6 flex items-center justify-between">
        <div className="font-oswald text-2xl uppercase tracking-wider">
          Lazy<span className="text-[#FF3333]">Lean</span>
        </div>
        <div className="text-[10px] font-manrope uppercase tracking-[0.15em] text-[#00CC66]">
          {paid ? "✓ Payment confirmed" : "✓ You're in"}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-[#FF3333] flex items-center justify-center mb-5">
            <CheckCircle size={36} weight="fill" className="text-white" />
          </div>
          <div className="id-label text-[#FF3333] mb-3">
            {paid ? "— PAYMENT CONFIRMED · BOOK YOUR CALL —" : "— BOOK YOUR ROADMAP CALL —"}
          </div>
          <h1 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-tight leading-[1.05] max-w-2xl mx-auto">
            Pick A Time.<br/>Let&apos;s Build Your Roadmap.
          </h1>
          <p className="text-zinc-300 font-manrope text-base md:text-lg mt-6 max-w-xl mx-auto">
            30 minutes on the phone with me. I&apos;ll build your personalised protocol live so you leave the call knowing exactly what to do for the next 30 days.
          </p>
        </div>

        {/* Calendly embed */}
        <div className="border border-[#262626] bg-[#0F0F0F] overflow-hidden" data-testid="calendly-embed-wrap">
          {CALENDLY_URL ? (
            <div
              className="calendly-inline-widget"
              data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=0a0a0a&text_color=ffffff&primary_color=ff3333`}
              style={{ minWidth: "320px", height: "700px" }}
              data-testid="calendly-inline-widget"
            />
          ) : (
            <div className="p-10 text-center">
              <CalendarCheck size={44} weight="bold" className="text-[#FF3333] mx-auto mb-4" />
              <div className="font-oswald text-xl uppercase tracking-wide mb-2">Calendly link coming soon</div>
              <p className="text-zinc-500 font-manrope text-sm max-w-md mx-auto">
                Coach Jimmy will email you within 24hrs with your booking link. Check your inbox for a message from{" "}
                <span className="text-white">jimmy@lazyleanmethod.com</span>.
              </p>
              <p className="text-zinc-600 font-manrope text-xs italic mt-4 max-w-md mx-auto">
                Coach: paste your Calendly URL into <code className="text-zinc-400">CALENDLY_URL</code> in JoinBookCallPage.jsx to auto-embed the booking widget here.
              </p>
            </div>
          )}
        </div>

        {!ready && CALENDLY_URL && (
          <div className="text-center text-zinc-500 text-xs font-manrope mt-3 italic">Loading Calendly...</div>
        )}

        <div className="mt-10 text-center border-t border-[#262626] pt-8">
          <p className="text-zinc-400 font-manrope text-sm mb-4">
            Booked your call (or want to book later)?
          </p>
          <Link
            to="/join/welcome"
            className="id-btn-primary px-8 py-4 text-sm inline-flex"
            data-testid="continue-to-welcome-btn"
          >
            Continue Into The App <ArrowRight size={16} weight="bold" />
          </Link>
          <div className="mt-3 text-[10px] text-zinc-600 font-manrope uppercase tracking-[0.15em]">
            You can always book later from your dashboard
          </div>
        </div>
      </div>
    </div>
  );
}
